import { useCallback, useState } from "react";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { authenticate, fetchAuthenticatedUser } from "../services";

const useAuthQuery = () => {
  const signIn = useSignIn();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setAuth] = useState(false);

  const verifyUserRole = (userData) => {
    // Check if user has admin role
    const hasRequiredRole = userData.roles?.some(role => 
      ["ROLE_ADMIN"].includes(role.roleName)
    );
    
    if (!hasRequiredRole) {
      throw new Error('Access restricted: Insufficient privileges');
    }
    return true;
  };

  const query = useCallback(
    async ({ username, password }) => {
      setLoading(true);
      setError(null);
      
      try {
        // Authenticate user
        const authData = await authenticate({ username, password });
        if (!authData || !authData.access_token) {
          throw new Error('Authentication failed: Could not retrieve access');
        }

        //Fetch authenticated user data
        const userData = await fetchAuthenticatedUser(authData.access_token);
        if (!userData || !userData.roles) {
          throw new Error('Authentication failed: User data not found');
        }
        
        //Verify user role
        verifyUserRole(userData);
        
        // Sign in if role is valid
        const authSuccess = signIn({
          auth: {
            token: authData.access_token,
            type: "Bearer",
            expiresIn: 21600,
            refreshToken: authData.refresh_token,
            refreshTokenExpireIn: 604800,
          },
          refresh: authData.refresh_token,
          userState: {
            access_token: authData.access_token,
            ...userData,
            roles: userData.roles, // Ensure roles are included in user state
          },
        });

        if (!authSuccess) {
          throw new Error('Authentication failed');
        }

        setAuth(true);
      } catch (error) {
        console.error('Authentication error:', error);
        setError(error?.response?.data?.errors[0] || error?.message || 'Authentication failed');
        // Handle specific error messages
        if (error.message.includes('Insufficient privileges')) {
          setError('Access denied: You do not have permission to access this system');
        }
      } finally {
        setLoading(false);
      }
    },
    [signIn]
  );

  return { loading, error, query, isAuthenticated, setError };
};

export default useAuthQuery;